import { Card, CardContent } from "components/Card"
import { AutoForm } from "components/Form/AutoForm"
import { getInitialValue } from "components/Form"
import { Page } from "components/Page"
import { TwoColLayout } from "components/TwoColLayout"
import { Wrapper } from "components/Wrapper"
import { Button } from "components/Button"
import { ArrowLeft, ChevronRight } from "components/Icon"
import { Tag, TagsList } from "components/Tag"
import { Modal, ModalActions, ModalContent } from "components/Modal"
import { useTranslator } from "components/Translator"
import { Text } from "components/Text"
import * as React from "react"
import { Pagination } from "components/Pagination"
import cx from "classnames"
import groupBy from "lodash/groupBy"
import { Separator } from "components/Separator"
import { Select, SingleValue } from "components/Select"
import { useRouter } from "components/Router"
import { useBreakpoints } from "hooks/useBreakpoints"
import { SearchListsTabs } from "components/SearchListsTabs"
import { RawLink } from "components/Link"
import { Publicity } from "components/Publicity"
import { IllustratedSection } from "components/IllustratedSection"
import { AutoField } from "@w3rone/json-schema-form"
import { PromoBanner } from "components/PromoBanner"
import { Markdown } from "components/Markdown"
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry"
import { FormControlLabel } from "components/Form/FormControlLabel"
import { Checkbox } from "components/Checkbox"
import { useConnectedUser } from "components/ConnectedUserProvider"

export const SearchPage = ({
  seoTitle,
  backgroundImage,
  formSearch,
  formSearchLight,
  formSave,
  filters,
  pagination,
  entity,
  panelsTitles,
  mainItems,
  secondaryItems,
  resultSuffix,
  popularSearches,
  savedSearches,
}) => {
  const translator = useTranslator()
  const { lg } = useBreakpoints()
  const connectedUser = useConnectedUser()

  const currentSearch = getCurrentSearch([
    ...(popularSearches ?? []),
    ...(savedSearches ?? []),
  ])

  React.useEffect(() => {
    const currentURL = window.location.toString()
    localStorage.setItem("previousSearch", currentURL)
  }, [])

  React.useEffect(() => {
    const id = window.location.hash?.slice(1)

    if (!id) return

    const element = document.getElementById(id)

    element?.scrollIntoView({ block: "start", behavior: "smooth" })
  }, [])

  return (
    <Page>
      <PromoBanner />
      <SearchPageHeader
        formSearch={formSearch}
        formSearchLight={formSearchLight}
        entity={entity}
        formSave={formSave}
        filters={filters}
        backgroundImage={backgroundImage}
        panelsTitles={panelsTitles}
        popularSearches={popularSearches}
        savedSearches={savedSearches}
      />
      <Wrapper>
        <section className="space-y-8 pt-8">
          <TwoColLayout
            publicityName={{
              mobile: "horizontalMobile",
            }}
            main={
              <div
                className={cx("space-y-10 lg:space-y-16", {
                  "scroll-mt-[175px]": connectedUser,
                  "scroll-mt-[100px]": !connectedUser,
                })}
                id="results"
              >
                <div className="space-y-6 lg:space-y-10">
                  <Text variant="headline4" tag="h1">
                    {seoTitle}
                  </Text>
                  <section className={"flex flex-col gap-4"}>
                    <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2 relative z-20">
                      <NbResultsSelect
                        options={formSearch.properties.nbResults.enum.map(
                          (value, index) => {
                            return {
                              value: value,
                              label:
                                formSearch.properties.nbResults.options.choice
                                  .enumTitles[index],
                              preferred: false,
                            }
                          },
                        )}
                        defaultValue={entity.nbResults}
                        name={`${formSearch.title}[nbResults]`}
                      />
                      <SortSelect
                        options={formSearch.properties.sort.enum.map(
                          (value, index) => {
                            return {
                              value: value,
                              label:
                                formSearch.properties.sort.options.choice
                                  .enumTitles[index],
                              preferred: false,
                            }
                          },
                        )}
                        defaultValue={entity.sort}
                        name={`${formSearch.title}[sort]`}
                      />
                    </div>

                    {formSearch.properties.userScopes ? (
                      <div className={"flex gap-2 flex-wrap"}>
                        <Text>
                          {translator.trans("SearchPage.userScopes.label")}
                        </Text>
                        <URLParamCheckboxes
                          options={formSearch.properties.userScopes.items.enum.map(
                            (value, index) => ({
                              value,
                              label:
                                formSearch.properties.userScopes.options.choice
                                  .enumTitles[index],
                            }),
                          )}
                          name={`${formSearch.title}[userScopes][]`}
                          defaultValue={entity.userScopes}
                        />
                      </div>
                    ) : null}
                  </section>
                  <Text tag="p" variant="body2">
                    <span className="text-light">
                      {translator.trans(
                        "SearchPage.resultsCount.caption",
                        null,
                        "components",
                      )}
                    </span>
                    <span className="font-bold">
                      {translator.trans(
                        "SearchPage.resultsCount.nb",
                        {
                          type: resultSuffix,
                          nb: pagination.totalItemCount.toLocaleString(
                            translator.locale,
                          ),
                        },
                        "components",
                      )}
                    </span>
                  </Text>
                </div>

                <div className={"hidden md:block"}>
                  <ResultsSection>{mainItems}</ResultsSection>
                </div>

                <div className={"md:hidden"}>
                  {mainItems.length > 0 ? (
                    <div className={"grid gap-5"}>
                      <ResultsSection>
                        {mainItems.slice(
                          0,
                          Math.ceil((mainItems.length - 1) / 2),
                        )}
                      </ResultsSection>
                      <div className={"md:hidden"}>
                        <Publicity name={"horizontalMobile"} />
                      </div>
                      <ResultsSection>
                        {mainItems.slice(Math.ceil((mainItems.length - 1) / 2))}
                      </ResultsSection>
                    </div>
                  ) : (
                    <Text tag="p">
                      {translator.trans(
                        "SearchPage.emptyMessage",
                        null,
                        "components",
                      )}
                    </Text>
                  )}{" "}
                </div>

                {secondaryItems?.length > 0 ? (
                  <ResultsSection
                    title={translator.trans(
                      "SearchPage.secondaryTitle",
                      null,
                      "components",
                    )}
                  >
                    {secondaryItems}
                  </ResultsSection>
                ) : null}
                <Pagination pageRange={2} {...pagination} />

                {currentSearch && currentSearch.content ? (
                  <section>
                    <Markdown>{currentSearch.content}</Markdown>
                  </section>
                ) : null}
              </div>
            }
            sidebar={null}
          />
          {lg ? <Publicity name="horizontal" /> : null}
        </section>
      </Wrapper>
    </Page>
  )
}

const SearchPageHeader = ({
  formSearch,
  formSearchLight,
  formSave,
  filters,
  entity,
  backgroundImage,
  panelsTitles,
  popularSearches,
  savedSearches,
}) => {
  const translator = useTranslator()

  /*
   * There  is a bug with the Modal component: when it is opened by default,
   * wherever you click in it, it closes. I can't find what's wrong for now,
   * so I use a workaround: it's closed by default, and opened in an effect
   * that is executed only when component mounts.
   */
  const [showModal, setShowModal] = React.useState(false)

  React.useEffect(() => {
    const url = new URL(window.location.toString())
    setShowModal(url.searchParams.get(ADVANCED_SEARCH_PARAM) === "true")
  }, [])

  const hasPredefinedSearches = [popularSearches, savedSearches].some(
    (array) => array && array.length > 0,
  )

  const [value, setValue] = React.useState({
    [formSearchLight.title]: getInitialValue(formSearch, entity),
  })

  return (
    <MaybeIllustratedSection image={backgroundImage}>
      <Wrapper className="py-6">
        <Card>
          {filters ? (
            <CardContent className="bg-grey-lighter">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Text variant="headline5">
                    {translator.trans(
                      "SearchPage.header.filters.title",
                      null,
                      "components",
                    )}
                  </Text>
                  {formSave ? (
                    <div>
                      <SaveSearchButton form={formSave} />
                    </div>
                  ) : null}
                </div>
                <TagsList>
                  {Object.entries(filters).map(([key, value]) => {
                    return <Tag key={key} label={value} />
                  })}
                </TagsList>
                <RawLink
                  className="cursor-pointer"
                  href={window.location.origin + window.location.pathname}
                >
                  <Text
                    variant="button"
                    className="text-primary-default underline"
                  >
                    {translator.trans(
                      "SearchPage.header.reset",
                      null,
                      "components",
                    )}
                  </Text>
                </RawLink>
              </div>
            </CardContent>
          ) : null}
          <CardContent>
            <div className="space-y-2">
              <SubmitButtonContext.Provider
                value={{ onAdvancedClick: () => setShowModal(true) }}
              >
                <AutoForm
                  schema={formSearchLight}
                  value={value}
                  onValueChange={setValue}
                  components={{
                    ActionsWrapper: SubmitButtonWrapper,
                    Actions: SubmitButton,
                  }}
                />
              </SubmitButtonContext.Provider>
            </div>
          </CardContent>
          {hasPredefinedSearches ? (
            <CardContent className="bg-grey-lighter">
              <SearchListsTabs
                popularSearches={{ status: "success", value: popularSearches }}
                savedSearches={
                  savedSearches
                    ? { status: "success", value: savedSearches }
                    : null
                }
              />
            </CardContent>
          ) : null}
        </Card>
      </Wrapper>
      <AdvancedSearch
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        form={formSearch}
        value={value}
        onValueChange={setValue}
        panelsTitles={panelsTitles}
      />
    </MaybeIllustratedSection>
  )
}

const SubmitButtonWrapper = ({ children }) => {
  return <div className="flex space-x-2 lg:justify-center">{children}</div>
}

const SubmitButton = () => {
  const translator = useTranslator()

  const ctx = React.useContext(SubmitButtonContext)

  return (
    <React.Fragment>
      <Button type="submit">
        {translator.trans(
          "SearchPage.header.form.submitLabel",
          null,
          "components",
        )}
      </Button>
      <Button
        type="button"
        variant="text"
        color="secondary"
        onClick={ctx?.onAdvancedClick}
      >
        {translator.trans(
          "SearchPage.header.form.advanced",
          null,
          "components",
        )}
      </Button>
    </React.Fragment>
  )
}

const SubmitButtonContext = React.createContext()

const MaybeIllustratedSection = ({ image, children }) => {
  if (image) {
    return <IllustratedSection image={image}>{children}</IllustratedSection>
  }

  return <section>{children}</section>
}

const SaveSearchButton = ({ form }) => {
  const translator = useTranslator()
  const [showModal, setShowModal] = React.useState(false)

  return (
    <>
      <button className="cursor-pointer" onClick={() => setShowModal(true)}>
        <Text variant="button" className="text-primary-default underline">
          {translator.trans("SearchPage.header.save", null, "components")}
        </Text>
      </button>
      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        title={translator.trans(
          "SearchPage.header.saveModal.title",
          null,
          "components",
        )}
      >
        <ModalContent>
          <AutoForm
            schema={form}
            submitLabel={translator.trans(
              "SearchPage.header.saveModal.submitLabel",
              null,
              "components",
            )}
            initialValue={{
              [form.title]: {
                ...getInitialValue(form, null),
                queryString: getSanitizedSearchQueryString(),
              },
            }}
            onSuccess={() => setShowModal(false)}
          />
        </ModalContent>
      </Modal>
    </>
  )
}

const ADVANCED_SEARCH_PARAM = "advanced_search"

const getSanitizedSearchQueryString = () => {
  const url = new URL(window.location.toString())
  url.searchParams.delete(ADVANCED_SEARCH_PARAM)

  return url.search
}

const ResultsSection = ({ title, children }) => {
  return (
    <section className="space-y-4 lg:space-y-6">
      {title ? <Text variant="headline4">{title}</Text> : null}
      <ResponsiveMasonry
        columnsCountBreakPoints={{ 1: 1, 750: 2 }}
        gutterBreakPoints={{ 1: "20px" }}
      >
        <Masonry>
          {React.Children.map(children, (child) => (
            <div key={child.key} className="min-w-0 w-full">
              {child}
            </div>
          ))}
        </Masonry>
      </ResponsiveMasonry>
    </section>
  )
}

const AdvancedSearch = ({
  isOpen,
  onRequestClose,
  form,
  value,
  onValueChange: onValueChangeProps,
  panelsTitles: panelsTitlesProps,
}) => {
  const translator = useTranslator()

  const requiredProperty = Object.entries(form.properties).find(
    ([_, property]) => property.options?.menuPanel === 0,
  )

  const requiredPropertyName = requiredProperty?.[0]

  const [panel, setPanel] = React.useState(requiredProperty ? "pre" : "summary")

  const requiredPropertyValue = requiredProperty
    ? value[form.title][requiredPropertyName]
    : null

  const propertiesByPanel =
    (requiredProperty && requiredPropertyValue) || !requiredProperty
      ? groupBy(Object.entries(form.properties), (entry) => {
          return requiredPropertyValue
            ? entry[1].options?.menuPanel?.[requiredPropertyValue]
            : entry[1].options?.menuPanel
        })
      : null

  if (propertiesByPanel) {
    delete propertiesByPanel["undefined"]
  }

  const panelsTitles = requiredPropertyValue
    ? panelsTitlesProps[requiredPropertyValue]
    : panelsTitlesProps

  const onValueChange = React.useCallback(
    (newValue) => {
      onValueChangeProps(newValue)
    },
    [onValueChangeProps],
  )

  function back() {
    setPanel((prevPanel) => {
      if (panel === "summary") {
        if (requiredPropertyValue) {
          return "pre"
        } else {
          return prevPanel
        }
      } else {
        if (panel === "pre") {
          return prevPanel
        } else {
          return "summary"
        }
      }
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={
        <AdvancedSearchTitle
          currentPanel={panel}
          requiresInitialValue={Boolean(requiredProperty)}
          panelsTitles={panelsTitles}
          onBack={back}
        />
      }
    >
      <ModalContent>
        <AutoForm
          schema={form}
          value={value}
          id="search-form"
          onValueChange={onValueChange}
        >
          {requiredProperty ? (
            <div className={cx({ hidden: panel !== "pre" })}>
              <AutoField name={`${form.title}[${requiredPropertyName}]`} />
            </div>
          ) : null}
          <div className={cx({ hidden: panel !== "summary" })}>
            <div className="flex flex-col space-y-4">
              {Object.keys(propertiesByPanel).map((key, index, arr) => {
                return (
                  <React.Fragment key={key}>
                    <button
                      type="button"
                      onClick={() => setPanel(key)}
                      className="text-left flex items-center"
                    >
                      <span className="grow">
                        <Text tag="span" variant="subtitle1">
                          {panelsTitles[key]}
                        </Text>
                      </span>
                      <span className="shrink-0">
                        <ChevronRight className="w-4 text-light" />
                      </span>
                    </button>
                    {index !== arr.length - 1 ? <Separator /> : null}
                  </React.Fragment>
                )
              })}
            </div>
          </div>
          {Object.entries(propertiesByPanel).map(([key, properties]) => {
            return (
              <div
                key={key}
                className={cx("space-y-6", { hidden: panel !== key })}
              >
                {properties.map(([name]) => {
                  return (
                    <AutoField key={name} name={`${form.title}[${name}]`} />
                  )
                })}
              </div>
            )
          })}
        </AutoForm>
      </ModalContent>
      <ModalActions>
        <div className="w-full flex">
          {panel === "pre" ? (
            <Button
              fullWidth
              type="button"
              onClick={() => setPanel("summary")}
              color="secondary"
              key="continue"
              disabled={!requiredPropertyValue}
            >
              {translator.trans(
                "SearchPage.advanced.continue",
                null,
                "components",
              )}
            </Button>
          ) : (
            <Button fullWidth type="submit" form="search-form" key="submit">
              {translator.trans(
                "SearchPage.advanced.submit",
                null,
                "components",
              )}
            </Button>
          )}
        </div>
      </ModalActions>
    </Modal>
  )
}

const AdvancedSearchTitle = ({
  currentPanel,
  requiresInitialValue,
  panelsTitles,
  onBack,
}) => {
  const translator = useTranslator()

  if (requiresInitialValue) {
    if (currentPanel === "pre") {
      return translator.trans("SearchPage.advanced.title", null, "components")
    } else {
      return (
        <div className="flex space-x-1 items-center">
          <Button
            type="button"
            variant="text"
            color="secondary"
            iconLeft={<ArrowLeft className="w-4" />}
            onClick={onBack}
          />
          <span>
            {currentPanel === "summary"
              ? translator.trans(
                  "SearchPage.advanced.title",
                  null,
                  "components",
                )
              : panelsTitles[currentPanel]}
          </span>
        </div>
      )
    }
  } else {
    if (currentPanel === "summary") {
      return translator.trans("SearchPage.advanced.title", null, "components")
    } else {
      return (
        <div className="flex space-x-1 items-center">
          <Button
            type="button"
            variant="text"
            color="secondary"
            iconLeft={<ArrowLeft className="w-4" />}
            onClick={onBack}
          />
          <span>
            {currentPanel === "summary"
              ? translator.trans(
                  "SearchPage.advanced.title",
                  null,
                  "components",
                )
              : panelsTitles[currentPanel]}
          </span>
        </div>
      )
    }
  }
}

const NbResultsSelect = ({ options, defaultValue, name }) => {
  return (
    <URLParamSelect
      options={options}
      defaultValue={defaultValue}
      name={name}
      components={{
        SingleValue: NbResultsSingleValue,
      }}
    />
  )
}

const NbResultsSingleValue = ({ children }) => {
  const translator = useTranslator()

  return (
    <SingleValue>
      {translator.trans(
        "SearchPage.display",
        { children: children },
        "components",
      )}
    </SingleValue>
  )
}

const SortSelect = ({ options, defaultValue, name }) => {
  return (
    <URLParamSelect
      options={options}
      defaultValue={defaultValue}
      name={name}
      components={{
        SingleValue: SortSingleValue,
      }}
    />
  )
}

const SortSingleValue = ({ children }) => {
  const translator = useTranslator()

  return (
    <SingleValue>
      {translator.trans(
        "SearchPage.filter",
        { children: children },
        "components",
      )}
    </SingleValue>
  )
}

const URLParamSelect = ({ options, defaultValue, name, components }) => {
  const router = useRouter()

  const defaultSelectedOption = options.find(
    (option) => option.value === defaultValue,
  )

  const handleChange = (selectedValue) => {
    const searchParams = new URLSearchParams(window.location.search)

    searchParams.set(name, selectedValue.value)
    router.redirect(window.location.pathname + "?" + searchParams.toString())
  }

  return (
    <Select
      options={options}
      defaultValue={defaultSelectedOption}
      components={components}
      onChange={handleChange}
    />
  )
}

const URLParamCheckboxes = ({ options, defaultValue, name }) => {
  const router = useRouter()

  const handleChange = (event) => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete(name)
    searchParams.delete("page")

    const newValue = event.target.checked
      ? Array.from([...defaultValue, event.target.value])
      : defaultValue.filter((value) => value !== event.target.value)

    for (const value of newValue) {
      searchParams.append(name, value)
    }

    router.redirect(window.location.pathname + "?" + searchParams.toString())
  }

  return (
    <div className={"flex gap-x-4 gap-y-2"}>
      {options.map((option) => (
        <FormControlLabel
          key={option.label}
          label={option.label}
          control={
            <Checkbox
              name={name}
              value={option.value}
              defaultChecked={defaultValue?.find(
                (value) => value === option.value,
              )}
              onChange={handleChange}
            />
          }
        />
      ))}
    </div>
  )
}

/**
 * @param {Array<{ id: number }>} searches
 */
function getCurrentSearch(searches) {
  const searchParams = new URLSearchParams(window.location.search)
  const ssoParam = searchParams.get("sso")

  if (!ssoParam) {
    return undefined
  }

  return searches.find((search) => search.id == ssoParam)
}
